/*
  Shoppable videos: enhances sections/shoppable-video-carousel.liquid.

  - Splide (already loaded by theme.liquid) drives the rail.
  - After the page loads (or once the section is near, on Save-Data and slow
    connections), the selected clip starts downloading. Once it plays fully
    buffered, the next clip preloads; never more than one ahead. Splide
    clones never own media.
  - One video plays at a time across every instance on the page.
  - Playback and auto-advance follow the state table in
    docs/sections/shoppable-video-carousel.md.

  Semantic hooks are dispatched as a bubbling `shoppable-video:event`
  CustomEvent with detail { name, payload }.
*/

(function () {
  const MAX_TARGET_WIDTH = 720;

  /**
   * Picks the smallest MP4 at least as wide as the rendered card times the
   * device pixel ratio (capped at 720px). Falls back to the largest smaller
   * MP4, then to the first MP4 when widths are missing.
   */
  function selectRendition(sources, cssWidth, dpr) {
    const mp4 = (Array.isArray(sources) ? sources : []).filter(
      (source) => source && typeof source.url === 'string' && source.url && (!source.type || /mp4/i.test(source.type))
    );
    if (!mp4.length) return null;
    const sized = mp4.filter((source) => Number(source.width) > 0).sort((a, b) => a.width - b.width);
    if (!sized.length) return mp4[0];
    const target = Math.min(Math.ceil((Number(cssWidth) || 0) * (Number(dpr) || 1)), MAX_TARGET_WIDTH);
    return sized.find((source) => source.width >= target) || sized[sized.length - 1];
  }

  window.ShoppableVideoCarouselUtils = { selectRendition };

  if (customElements.get('shoppable-video-carousel')) return;

  const SPLIDE_WAIT_MS = 5000;
  const STALL_TIMEOUT_MS = 10000;
  const DESKTOP_QUERY = '(min-width: 990px)';
  const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  const SLOW_CONNECTIONS = ['slow-2g', '2g', '3g'];

  // Runs the callback once the page has loaded and the browser is idle, so
  // video downloads never compete with the rest of the page.
  function afterPageLoad(callback, signal) {
    const idle = () => {
      if (signal.aborted) return;
      if ('requestIdleCallback' in window) window.requestIdleCallback(callback, { timeout: 3000 });
      else setTimeout(callback, 200);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true, signal });
  }

  // Page-level owner of playback: at most one section plays at a time.
  const coordinator = {
    owner: null,
    instances: new Set(),
    claim(instance) {
      if (this.owner && this.owner !== instance) this.owner.yieldPlayback();
      this.owner = instance;
    },
    release(instance, notifyOthers) {
      if (this.owner !== instance) return;
      this.owner = null;
      if (notifyOthers) this.instances.forEach((other) => other !== instance && other.sync());
    },
    busy(instance) {
      return Boolean(this.owner && this.owner !== instance && this.owner.isPlaying());
    },
  };

  class ShoppableVideoCarousel extends HTMLElement {
    connectedCallback() {
      if (this.mounted) return;
      this.root = this.querySelector('.svc__carousel');
      if (!this.root) return;
      this.mounted = true;

      this.abort = new AbortController();
      this.slides = Array.from(this.root.querySelectorAll('.svc__slide'));
      this.cards = this.slides.map((slide, index) => this.readCard(slide, index));
      this.count = this.cards.length;
      this.status = this.querySelector('.svc__status');
      this.arrows = this.querySelector('.svc__arrows');
      this.timerSeconds = Number(this.getAttribute('data-timer')) || 0;
      this.designMode = this.getAttribute('data-design') === 'true' || Boolean(window.Shopify && window.Shopify.designMode);

      this.active = 0;
      this.activation = 0;
      this.advancedFor = -1;
      this.intent = 'auto'; // 'auto' | 'user-play' | 'user-pause'
      this.soundOn = false;
      this.near = false;
      this.inView = false;
      this.docVisible = !document.hidden;
      this.autoplayDenied = false;
      this.panelCard = null;
      this.hover = false;
      this.keyboardFocus = false;
      this.dragging = false;
      this.pendingAdvance = false;
      this.moveReason = 'manual';
      this.timer = null;
      this.pageLoaded = false;

      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.reduced = window.matchMedia(REDUCED_QUERY);

      coordinator.instances.add(this);
      this.bindEvents();
      this.waitForSplide(Date.now());
      afterPageLoad(() => {
        this.pageLoaded = true;
        this.sync();
      }, this.abort.signal);
    }

    disconnectedCallback() {
      if (!this.mounted) return;
      this.mounted = false;
      this.abort.abort();
      clearTimeout(this.splideWait);
      clearTimeout(this.resizeTimer);
      this.clearTimer();
      if (this.nearObserver) this.nearObserver.disconnect();
      if (this.viewObserver) this.viewObserver.disconnect();
      this.cards.forEach((card) => {
        clearTimeout(card.stallTimer);
        card.video.pause();
      });
      coordinator.release(this, true);
      coordinator.instances.delete(this);
      if (this.splide) this.splide.destroy(true);
      this.splide = null;
      if (this.probe) this.probe.remove();
      this.classList.remove('svc--enhanced');
    }

    readCard(slide, index) {
      const sourcesEl = slide.querySelector('.svc-card__sources');
      let sources = [];
      try {
        sources = JSON.parse(sourcesEl ? sourcesEl.textContent : '[]');
      } catch (error) {
        sources = [];
      }
      const productsEl = slide.querySelector('.svc-products');
      const video = slide.querySelector('.svc-card__video');
      return {
        index,
        slide,
        blockId: slide.getAttribute('data-block'),
        title: video.getAttribute('aria-label') || '',
        frame: slide.querySelector('.svc-card'),
        media: slide.querySelector('.svc-card__media'),
        video,
        activate: slide.querySelector('.svc-card__activate'),
        soundButton: slide.querySelector('.svc-card__control--sound'),
        playButton: slide.querySelector('.svc-card__control--play'),
        errorEl: slide.querySelector('.svc-card__error'),
        productsEl,
        toggle: productsEl && productsEl.querySelector('.svc-products__toggle'),
        productList: productsEl && productsEl.querySelector('.svc-products__list'),
        sources,
        attached: false,
        unplayable: false,
        error: false,
        ended: false,
        playPending: false,
        playEmittedFor: -1,
        stallTimer: null,
      };
    }

    // ---------- Setup ----------

    bindEvents() {
      const options = { signal: this.abort.signal };

      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hover = true;
        this.onInteractionChange();
      }, options);
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hover = false;
        this.onInteractionChange();
      }, options);
      this.addEventListener('focusin', (event) => {
        this.keyboardFocus = Boolean(event.target.matches && event.target.matches(':focus-visible'));
        this.onInteractionChange();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.keyboardFocus = false;
        this.onInteractionChange();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest && event.target.closest('.svc__slide');
        if (!slide) return;
        this.goToIndex(Number(slide.getAttribute('data-index')), 'manual');
      }, options);

      document.addEventListener('visibilitychange', () => {
        this.docVisible = !document.hidden;
        this.sync();
      }, options);
      this.reduced.addEventListener('change', () => {
        if (this.splide) this.splide.options = { speed: this.reduced.matches ? 0 : 500 };
        this.sync();
      }, options);
      this.desktop.addEventListener('change', () => this.sync(), options);
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.onResize(), 150);
      }, options);

      this.cards.forEach((card) => {
        const video = card.video;
        video.addEventListener('playing', () => this.onPlaying(card), options);
        video.addEventListener('canplaythrough', () => {
          if (card.index === this.active) this.preloadNext();
        }, options);
        video.addEventListener('pause', () => this.onMediaPause(card), options);
        video.addEventListener('ended', () => this.onEnded(card), options);
        video.addEventListener('error', () => this.onMediaError(card), options);
        video.addEventListener('waiting', () => this.watchStall(card), options);
        video.addEventListener('stalled', () => this.watchStall(card), options);
        video.addEventListener('timeupdate', () => {
          clearTimeout(card.stallTimer);
          card.stallTimer = null;
        }, options);
        if (card.productList) {
          card.productList.addEventListener('wheel', (event) => event.stopPropagation(), options);
        }
      });
    }

    waitForSplide(startedAt) {
      if (!this.mounted) return;
      if (typeof window.Splide === 'function') {
        this.mountSplide();
        return;
      }
      if (Date.now() - startedAt > SPLIDE_WAIT_MS) return; // stay a static list
      this.splideWait = setTimeout(() => this.waitForSplide(startedAt), 50);
    }

    measure() {
      if (!this.probe) {
        this.probe = document.createElement('div');
        this.probe.className = 'svc__probe';
        this.probe.setAttribute('aria-hidden', 'true');
        this.probe.style.cssText =
          'position:absolute;visibility:hidden;height:0;width:var(--svc-card-w);margin-right:var(--svc-gap);pointer-events:none;';
        this.appendChild(this.probe);
      }
      const style = window.getComputedStyle(this.probe);
      return {
        cardWidth: this.probe.getBoundingClientRect().width || 300,
        gap: parseFloat(style.marginRight) || 0,
        railWidth: this.clientWidth || window.innerWidth,
      };
    }

    needsLoop(size) {
      if (this.count < 2) return false;
      return this.count * size.cardWidth + (this.count - 1) * size.gap > size.railWidth;
    }

    mountSplide() {
      // Leave the native-scroll fallback before measuring: the scroll-snapped
      // track may be scrolled, which would offset Splide's centering.
      this.classList.add('svc--enhanced');
      const track = this.root.querySelector('.splide__track');
      track.scrollLeft = 0;
      if (!this.trackScrollBound) {
        this.trackScrollBound = true;
        // Focusing a partly hidden card can scroll the clipped track; undo it.
        track.addEventListener('scroll', () => {
          if (this.splide) track.scrollLeft = 0;
        }, { signal: this.abort.signal });
      }
      const size = this.measure();
      this.size = size;
      this.loop = this.needsLoop(size);
      try {
        this.splide = new window.Splide(this.root, this.splideOptions(size));
        this.splide.on('mounted refresh', () => this.sanitizeClones());
        this.splide.on('visible hidden', (slide) => {
          if (slide.isClone) this.lockClone(slide.slide);
        });
        this.splide.on('move', (index) => this.onMove(index));
        this.splide.on('moved', () => this.updateArrows());
        this.splide.on('drag', () => {
          this.dragging = true;
          this.closePanel(false);
          this.onInteractionChange();
        });
        this.splide.on('dragged', () => {
          this.dragging = false;
          this.onInteractionChange();
        });
        this.splide.mount();
      } catch (error) {
        console.error('Shoppable videos: carousel failed to start', error);
        if (this.splide) this.splide.destroy(true);
        this.splide = null;
        this.classList.remove('svc--enhanced');
        return;
      }

      if (this.arrows && this.count > 1) this.arrows.hidden = false;
      this.updateArrows();
      this.observe();
      this.bindNavigation();
      this.sync();
    }

    splideOptions(size) {
      return {
        type: this.loop ? 'loop' : 'slide',
        start: this.active,
        fixedWidth: size.cardWidth,
        gap: size.gap,
        focus: 'center',
        trimSpace: false,
        perMove: 1,
        speed: this.reduced.matches ? 0 : 500,
        easing: 'ease',
        arrows: false,
        pagination: false,
        keyboard: false,
        slideFocus: false,
        updateOnMove: true,
        drag: this.count > 1,
        // Splide adds flick momentum on top of the drag distance. Capping it at
        // half a card means a short flick reaches the next card but never skips one.
        flickMaxPages: this.flickLimit(size),
        noDrag: '.svc-products, .svc-products *, .svc-card__controls, .svc-card__controls *, .svc-card__error, .svc-card__error *',
        i18n: { carousel: 'carousel', slide: 'video', slideLabel: '%s of %s' },
      };
    }

    flickLimit(size) {
      return Math.min(1, (size.cardWidth + size.gap) / 2 / size.railWidth);
    }

    remount() {
      if (!this.splide) return;
      this.splide.destroy(true);
      this.splide = null;
      this.mountSplide();
    }

    onResize() {
      if (!this.splide) return;
      const size = this.measure();
      if (this.needsLoop(size) !== this.loop) {
        this.remount();
        return;
      }
      if (size.cardWidth !== this.size.cardWidth || size.gap !== this.size.gap) {
        this.splide.options = { fixedWidth: size.cardWidth, gap: size.gap, flickMaxPages: this.flickLimit(size) };
      } else if (size.railWidth !== this.size.railWidth) {
        this.splide.options = { flickMaxPages: this.flickLimit(size) };
      }
      this.size = size;
    }

    bindNavigation() {
      if (this.navigationBound) return;
      this.navigationBound = true;
      const options = { signal: this.abort.signal };
      const prev = this.querySelector('.svc__arrow--prev');
      const next = this.querySelector('.svc__arrow--next');
      if (prev) prev.addEventListener('click', () => this.go('<', 'manual'), options);
      if (next) next.addEventListener('click', () => this.go('>', 'manual'), options);

      // Horizontal trackpad swipes move one card. Vertical wheel scrolling is left alone.
      let accumulated = 0;
      let lockedUntil = 0;
      this.root.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 2) return;
        event.preventDefault();
        const now = Date.now();
        if (now < lockedUntil) return;
        accumulated += event.deltaX;
        if (Math.abs(accumulated) >= 40) {
          this.go(accumulated > 0 ? '>' : '<', 'manual');
          accumulated = 0;
          lockedUntil = now + 450;
        }
      }, { signal: this.abort.signal, passive: false });
    }

    sanitizeClones() {
      this.root.querySelectorAll('.splide__slide--clone').forEach((clone) => {
        if (clone.hasAttribute('data-clone')) return;
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('video, script').forEach((el) => el.remove());
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        clone.querySelectorAll('[aria-controls]').forEach((el) => el.removeAttribute('aria-controls'));
        this.lockClone(clone);
        const frame = clone.querySelector('.svc-card');
        if (frame) frame.classList.remove('svc-card--frame', 'svc-card--idle');
      });
    }

    // Splide re-enables focus on slides that scroll into view; clones stay inert to keyboards.
    lockClone(clone) {
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    }

    observe() {
      if (this.nearObserver) return;
      this.nearObserver = new IntersectionObserver((entries) => {
        this.near = entries[entries.length - 1].isIntersecting;
        this.sync();
      }, { rootMargin: '100% 0px 100% 0px' });
      this.nearObserver.observe(this);

      this.viewObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (entry.target !== this.cards[this.active].media) return;
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        this.sync();
      }, { threshold: [0, 0.5, 1] });
      this.viewObserver.observe(this.cards[this.active].media);
    }

    // ---------- Navigation ----------

    go(control, reason) {
      if (!this.splide || this.count < 2) return;
      this.closePanel(false);
      this.moveReason = reason;
      this.splide.go(control);
      this.moveReason = 'manual';
    }

    goToIndex(index, reason) {
      if (!(index >= 0 && index < this.count)) return;
      if (!this.splide) {
        this.active = index;
        return;
      }
      if (index === this.active) return;
      this.go(index, reason);
    }

    goToSlideElement(slide, reason) {
      const slides = this.splide.Components.Slides.get(true);
      const match = slides.find((item) => item.slide === slide);
      if (!match) return;
      const delta = match.index - this.splide.index;
      if (delta === 0) return;
      this.go(delta > 0 ? '+' + delta : '-' + -delta, reason);
    }

    onMove(index) {
      if (index === this.active) return;
      const reason = this.moveReason;
      const previous = this.cards[this.active];

      this.closePanel(false);
      this.deactivate(previous);
      if (this.viewObserver) {
        this.viewObserver.unobserve(previous.media);
        this.viewObserver.observe(this.cards[index].media);
      }

      this.active = index;
      this.activation += 1;
      this.pendingAdvance = false;
      this.intent = 'auto';
      this.soundOn = false;
      this.clearTimer();

      const card = this.cards[index];
      card.ended = false;
      this.updateArrows();
      this.emit('slide_change', { reason });
      if (reason === 'manual' && this.status) {
        this.status.textContent = 'Video ' + (index + 1) + ' of ' + this.count + ': ' + card.title;
      }
      this.sync();
    }

    deactivate(card) {
      clearTimeout(card.stallTimer);
      if (!card.video.paused) {
        card.video.pause();
        this.emit('video_pause', { reason: 'navigation' }, card);
      }
      if (card.video.currentTime > 0) {
        try {
          card.video.currentTime = 0;
        } catch (error) {
          /* not seekable yet */
        }
      }
      card.video.muted = true;
      card.playPending = false;
      card.frame.classList.remove('svc-card--frame');
    }

    advance(reason, activation) {
      if (activation !== this.activation || this.advancedFor === activation) return;
      if (!this.canAutoAdvance()) {
        if (reason === 'ended') this.pendingAdvance = true;
        return;
      }
      this.advancedFor = activation;
      this.pendingAdvance = false;
      this.clearTimer();
      if (this.loop) this.go('>', reason);
      else this.go((this.active + 1) % this.count, reason);
    }

    updateArrows() {
      if (!this.arrows) return;
      const prev = this.arrows.querySelector('.svc__arrow--prev');
      const next = this.arrows.querySelector('.svc__arrow--next');
      prev.disabled = !this.loop && this.active === 0;
      next.disabled = !this.loop && this.active === this.count - 1;
    }

    // ---------- Input ----------

    onClick(event) {
      const target = event.target;
      if (!this.splide || !target.closest) return;

      const productLink = target.closest('a[data-product]');
      if (productLink) {
        const slide = productLink.closest('.svc__slide');
        const card = slide && !slide.hasAttribute('data-clone') ? this.cards[Number(slide.getAttribute('data-index'))] : null;
        this.emit('product_click', { product_id: productLink.getAttribute('data-product') }, card || undefined);
        return; // native navigation, modifier keys included
      }
      if (target.closest('a')) return;

      const slide = target.closest('.svc__slide');
      if (!slide) return;

      if (slide.hasAttribute('data-clone')) {
        this.goToSlideElement(slide, 'manual');
        return;
      }

      const card = this.cards[Number(slide.getAttribute('data-index'))];
      if (!card) return;

      if (target.closest('.svc-products__toggle')) {
        if (this.panelCard === card) this.closePanel(true);
        else this.openPanel(card);
        return;
      }
      if (target.closest('.svc-products')) return;

      if (target.closest('.svc-card__retry')) {
        this.retry(card);
        return;
      }

      if (card.index !== this.active) {
        this.goToSlideElement(slide, 'manual');
        return;
      }

      if (target.closest('.svc-card__control--sound')) {
        this.toggleSound();
      } else if (target.closest('.svc-card__control--play')) {
        this.togglePlay();
      } else if (target.closest('.svc-card__activate')) {
        if (this.isPlaying()) this.toggleSound();
        else this.togglePlay();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.panelCard) {
        event.preventDefault();
        this.closePanel(true);
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target;
      if (!target.closest || !target.closest('.svc__arrow, .svc-card__activate')) return;
      event.preventDefault();
      this.go(event.key === 'ArrowRight' ? '>' : '<', 'manual');
      if (target.classList.contains('svc-card__activate')) {
        this.cards[this.active].playButton.focus({ preventScroll: true });
      }
    }

    onInteractionChange() {
      if (!this.interacting() && this.pendingAdvance) {
        this.advance('ended', this.activation);
        return;
      }
      this.updateTimer();
    }

    interacting() {
      return this.hover || this.keyboardFocus || this.dragging;
    }

    toggleSound() {
      const card = this.cards[this.active];
      this.soundOn = !this.soundOn;
      card.video.muted = !this.soundOn;
      if (this.soundOn && !this.isPlaying()) this.requestPlay(card);
      this.sync();
    }

    togglePlay() {
      const card = this.cards[this.active];
      if (this.isPlaying() || card.playPending) {
        this.intent = 'user-pause';
      } else {
        this.requestPlay(card);
      }
      this.sync();
    }

    requestPlay(card) {
      this.intent = 'user-play';
      this.autoplayDenied = false;
      if (card.ended) {
        card.ended = false;
        try {
          card.video.currentTime = 0;
        } catch (error) {
          /* ignore */
        }
      }
    }

    retry(card) {
      card.error = false;
      card.errorEl.hidden = true;
      if (card.attached) card.video.load();
      this.intent = 'user-play';
      this.sync();
    }

    // ---------- Product panel ----------

    openPanel(card) {
      if (!card.toggle) return;
      if (this.panelCard) this.closePanel(false);
      this.panelCard = card;
      card.productsEl.classList.add('svc-products--expanded');
      card.productList.querySelectorAll('.svc-product').forEach((row) => {
        row.hidden = false;
      });
      card.toggle.setAttribute('aria-expanded', 'true');
      card.toggle.setAttribute('aria-label', card.toggle.getAttribute('aria-label').replace(/^Show all/, 'Hide all'));
      this.emit('product_list_open', {}, card);
      this.sync();
    }

    closePanel(restoreFocus) {
      const card = this.panelCard;
      if (!card) return;
      this.panelCard = null;
      card.productsEl.classList.remove('svc-products--expanded');
      card.productList.scrollTop = 0;
      card.productList.querySelectorAll('.svc-product').forEach((row, index) => {
        row.hidden = index > 0;
      });
      card.toggle.setAttribute('aria-expanded', 'false');
      card.toggle.setAttribute('aria-label', card.toggle.getAttribute('aria-label').replace(/^Hide all/, 'Show all'));
      if (restoreFocus) card.toggle.focus({ preventScroll: true });
      this.sync();
    }

    // ---------- Playback ----------

    isPlaying() {
      const card = this.cards && this.cards[this.active];
      return Boolean(card && !card.video.paused && !card.video.ended);
    }

    saveData() {
      return Boolean(navigator.connection && navigator.connection.saveData);
    }

    shouldPlay(card) {
      if (!this.splide || !this.near || !this.inView || !this.docVisible) return false;
      if (this.panelCard || card.error || card.unplayable) return false;
      if (this.intent === 'user-pause') return false;
      if (this.intent === 'user-play') return true;
      if (this.reduced.matches || this.saveData() || this.designMode || this.autoplayDenied || card.ended) return false;
      return !coordinator.busy(this);
    }

    pauseReason(card) {
      if (!this.inView || !this.near) return 'offscreen';
      if (!this.docVisible) return 'hidden';
      if (this.panelCard) return 'product_list';
      if (card.error) return 'error';
      if (this.intent === 'user-pause') return 'user';
      return 'other';
    }

    canAutoAdvance() {
      return (
        this.count > 1 &&
        this.near &&
        this.inView &&
        this.docVisible &&
        !this.panelCard &&
        !this.interacting() &&
        this.intent !== 'user-pause' &&
        !this.designMode &&
        !this.reduced.matches &&
        !this.saveData() &&
        !this.cards[this.active].error
      );
    }

    sync() {
      if (!this.mounted || !this.splide) return;
      const card = this.cards[this.active];

      // After the page loads, fetch the start of the selected clip (metadata and
      // first frames, well under a megabyte) so it can start quickly. Buffer it
      // fully only once the section is near, since browsers buffer 10+ MB for
      // preload="auto". Without preloading, wait for the section to be near.
      if (this.canPreload() && this.near) this.attach(card, 'auto');
      else if (this.canPreload() && this.pageLoaded) this.attach(card, 'metadata');
      else if (this.near) this.attach(card, 'none');

      if (this.shouldPlay(card)) {
        this.play(card);
      } else if (this.isPlaying() || card.playPending) {
        const reason = this.pauseReason(card);
        const wasPlaying = this.isPlaying();
        card.playPending = false;
        card.video.pause();
        if (wasPlaying) this.emit('video_pause', { reason }, card);
        coordinator.release(this, reason === 'offscreen' || reason === 'hidden');
      }

      this.updateTimer();
      this.render();
    }

    // Save-Data, slow connections, reduced motion, and the theme editor never
    // download video ahead of an actual play.
    canPreload() {
      const connection = navigator.connection;
      const slow = Boolean(connection && SLOW_CONNECTIONS.includes(connection.effectiveType));
      return !this.saveData() && !slow && !this.reduced.matches && !this.designMode;
    }

    // Once the selected clip is playing and fully buffered, fetch the next clip
    // in line so it starts immediately. Never more than one clip ahead.
    preloadNext() {
      if (this.count < 2 || !this.canPreload() || !this.isPlaying()) return;
      const card = this.cards[this.active];
      if (card.video.readyState < 4) return;
      this.attach(this.cards[(this.active + 1) % this.count], 'auto');
    }

    attach(card, preload) {
      if (card.unplayable) return;
      if (card.attached) {
        if (preload === 'auto' && card.video.preload !== 'auto') card.video.preload = 'auto';
        return;
      }
      const width = card.media.getBoundingClientRect().width || (this.size && this.size.cardWidth) || 300;
      const source = selectRendition(card.sources, width, window.devicePixelRatio || 1);
      if (!source) {
        card.unplayable = true;
        card.frame.classList.add('svc-card--unplayable');
        return;
      }
      card.video.preload = preload || 'none';
      card.video.src = source.url;
      card.attached = true;
    }

    play(card) {
      if (!card.attached || card.playPending || this.isPlaying()) return;
      coordinator.claim(this);
      card.video.muted = !this.soundOn;
      card.playPending = true;
      const activation = this.activation;
      let promise;
      try {
        promise = card.video.play();
      } catch (error) {
        promise = Promise.reject(error);
      }
      if (!promise || typeof promise.then !== 'function') return;
      promise.then(
        () => {
          card.playPending = false;
          this.render();
        },
        (error) => {
          card.playPending = false;
          if (activation !== this.activation || !this.mounted) return;
          if (error && error.name === 'NotAllowedError') {
            if (this.intent === 'auto') this.autoplayDenied = true;
            else this.intent = 'user-pause';
          } else if (!error || error.name !== 'AbortError') {
            this.setError(card);
            return;
          }
          this.sync();
        }
      );
    }

    yieldPlayback() {
      const card = this.cards[this.active];
      if (this.isPlaying() || card.playPending) {
        card.playPending = false;
        card.video.pause();
        this.emit('video_pause', { reason: 'other_video' }, card);
      }
      this.clearTimer();
      this.render();
    }

    onPlaying(card) {
      if (card.index !== this.active) {
        card.video.pause();
        return;
      }
      clearTimeout(card.stallTimer);
      card.stallTimer = null;
      card.playPending = false;
      card.frame.classList.add('svc-card--frame');
      if (card.playEmittedFor !== this.activation) {
        card.playEmittedFor = this.activation;
        this.emit('video_play', {}, card);
      }
      if (!this.shouldPlay(card)) {
        this.sync();
        return;
      }
      this.updateTimer();
      this.render();
      this.preloadNext();
    }

    onMediaPause(card) {
      if (card.index !== this.active) return;
      this.updateTimer();
      this.render();
    }

    onEnded(card) {
      if (card.index !== this.active) return;
      card.ended = true;
      this.clearTimer();
      this.emit('video_complete', {}, card);
      coordinator.release(this, false);
      if (this.count > 1) this.advance('ended', this.activation);
      this.render();
    }

    onMediaError(card) {
      if (!card.attached || card.index !== this.active) return;
      this.setError(card);
    }

    watchStall(card) {
      if (card.index !== this.active || card.stallTimer || card.video.paused) return;
      card.stallTimer = setTimeout(() => {
        card.stallTimer = null;
        if (card.index === this.active && card.video.readyState < 3) this.setError(card);
      }, STALL_TIMEOUT_MS);
    }

    setError(card) {
      clearTimeout(card.stallTimer);
      card.stallTimer = null;
      if (card.error) return;
      card.error = true;
      card.errorEl.hidden = false;
      this.sync();
    }

    updateTimer() {
      if (!this.mounted) return;
      const eligible =
        this.timerSeconds > 0 &&
        this.desktop.matches &&
        this.intent === 'auto' &&
        !this.soundOn &&
        this.isPlaying() &&
        this.canAutoAdvance();
      if (eligible && !this.timer) {
        const activation = this.activation;
        this.timer = setTimeout(() => {
          this.timer = null;
          this.advance('timer', activation);
        }, this.timerSeconds * 1000);
      } else if (!eligible) {
        this.clearTimer();
      }
    }

    clearTimer() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    render() {
      const playing = this.isPlaying();
      this.cards.forEach((card) => {
        const active = card.index === this.active;
        const busy = active && (playing || card.playPending);
        card.frame.classList.toggle('svc-card--idle', active && !busy && !card.error && !card.unplayable);
        card.playButton.setAttribute('aria-pressed', String(busy));
        card.soundButton.setAttribute('aria-pressed', String(active && this.soundOn));
        if (active) {
          card.activate.setAttribute('tabindex', '-1');
          card.activate.setAttribute('aria-hidden', 'true');
        } else {
          card.activate.removeAttribute('tabindex');
          card.activate.removeAttribute('aria-hidden');
        }
      });
    }

    // ---------- Hooks ----------

    emit(name, extra, card) {
      const target = card || this.cards[this.active];
      const payload = Object.assign(
        {
          section_id: this.getAttribute('data-section'),
          block_id: target.blockId,
          index: target.index,
        },
        extra
      );
      this.dispatchEvent(new CustomEvent('shoppable-video:event', { bubbles: true, detail: { name, payload } }));
    }
  }

  customElements.define('shoppable-video-carousel', ShoppableVideoCarousel);
})();
